'use client';

import { useState, useEffect } from 'react';
import { formatBengaliDateTime, formatRelativeBengaliDateTime } from '@/lib/dateUtils';

interface AnimatedDetailTimeProps {
    dateStr?: string;
    isManual?: boolean;
    className?: string;
}

export default function AnimatedDetailTime({ dateStr, isManual, className = '' }: AnimatedDetailTimeProps) {
    if (!dateStr) return null;

    const adjustedDate = new Date(dateStr);
    if (!isManual) {
        adjustedDate.setMinutes(adjustedDate.getMinutes() - 15);
    }

    const now = new Date();
    const diffMs = now.getTime() - adjustedDate.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const isWithin24Hours = diffMs >= 0 && diffMs < oneDayMs;

    const absoluteTime = formatBengaliDateTime(dateStr, !!isManual);
    const relativeTime = formatRelativeBengaliDateTime(dateStr, !!isManual);

    // If older than 24 hours, render static absolute time without animation loop
    if (!isWithin24Hours) {
        return <span className={className}>{absoluteTime}</span>;
    }

    // State A (relative) vs State B (absolute) toggle every 4 seconds
    const [showRelative, setShowRelative] = useState(true);
    const [fade, setFade] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setFade(false); // start fade out
            setTimeout(() => {
                setShowRelative(prev => !prev);
                setFade(true); // fade in
            }, 300); // 300ms transition match
        }, 4000);

        return () => clearInterval(interval);
    }, []);

    return (
        <span
            className={`inline-block transition-all duration-300 transform ${
                fade ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
            } ${className}`}
        >
            {showRelative ? relativeTime : absoluteTime}
        </span>
    );
}
